/**
 * TanStack Query хуки для работы с событиями безопасности.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchEvent, fetchEvents, fetchEventSample, fetchEventStatistic } from '@/api/events';
import type { EventFilters } from '@/api/events';
import type { EntityId } from '@/lib/entityId';
import { usePaginatedQuery } from './usePaginatedQuery';

/** Ключи кеша для событий */
export const eventKeys = {
	all: ['events'] as const,
	list: (filters?: EventFilters) => [...eventKeys.all, 'list', filters] as const,
	detail: (id: EntityId) => [...eventKeys.all, 'detail', id] as const,
	sample: (id: EntityId) => [...eventKeys.all, 'sample', id] as const,
	stats: (filters?: Omit<EventFilters, 'limit' | 'offset'>) =>
		[...eventKeys.all, 'stats', filters] as const,
};

/** Список событий с пагинацией */
export function useEvents(filters?: Omit<EventFilters, 'limit' | 'offset'>) {
	return usePaginatedQuery({
		queryKey: eventKeys.all,
		queryFn: async (params) => {
			const response = await fetchEvents({
				...filters,
				limit: params.limit,
				offset: params.offset,
				sort: params.sort,
			});
			return {
				items: response.log_events,
				total: response.total_filtered,
				totalFiltered: response.total_filtered,
				overallTotal: response.total,
			};
		},
		filters: filters as Record<string, string | number | boolean | undefined>,
		initialSort: 'created_at:desc',
	});
}

/** Одно событие по id */
export function useEvent(id: EntityId | undefined) {
	return useQuery({
		queryKey: eventKeys.detail(id!),
		queryFn: () => fetchEvent(id!),
		enabled: id !== undefined,
	});
}

/** Sample данных события */
export function useEventSample(eventId: EntityId | undefined) {
	return useQuery({
		queryKey: eventKeys.sample(eventId!),
		queryFn: () => fetchEventSample(eventId!),
		enabled: eventId !== undefined,
	});
}

/** Статистика событий (счётчики по severity) */
export function useEventStats(filters?: Omit<EventFilters, 'limit' | 'offset'>) {
	return useQuery({
		queryKey: eventKeys.stats(filters),
		queryFn: () => fetchEventStatistic(filters),
	});
}
